/** One brace group, allowing one level of nesting (e.g. `{500{,}000}`). */
const BRACE_ARG = String.raw`\{(?:[^{}]|\{[^{}]*\})*\}`;
/** TeX command with optional brace args: `\dfrac{a}{b}`, `\approx`, `\text{Rs }`. */
const TEX_CMD = String.raw`\\[a-zA-Z]+\*?(?:${BRACE_ARG})*`;
/** Thin/med/thick space or escaped percent/dollar. */
const TEX_ESC = String.raw`\\[,;!% $]`;
/** Number that may use TeX thousands separators: `38{,}461.54`. */
const TEX_NUM = String.raw`[0-9]+(?:\{,\}[0-9]+)*(?:\.[0-9]+)?`;
const TEX_TOKEN = String.raw`(?:${TEX_CMD}|${TEX_ESC}|${TEX_NUM}|[+\-=()≈×·÷/^_])`;
/**
 * Bare inline TeX: starts with a command, then more math tokens.
 * Example: `\dfrac{500{,}000}{13} \approx 38{,}461.54`
 */
const BARE_TEX_RE = new RegExp(`${TEX_CMD}(?:\\s*${TEX_TOKEN})*`, 'g');

/**
 * Escapes bare `%` in TeX (comment char) so KaTeX does not truncate formulas
 * like `\text{Tax at 10%}`. Leaves already-escaped `\%` alone.
 */
export function escapeBarePercentInTex(tex: string): string {
  return tex.replace(/(?<!\\)%/g, '\\%');
}

function protectMathRegions(text: string): { text: string; slots: string[] } {
  const slots: string[] = [];
  const replaced = text
    .replace(/\$\$([\s\S]+?)\$\$/g, (_full, body: string) => {
      const i = slots.length;
      slots.push(`$$${escapeBarePercentInTex(body)}$$`);
      return `\0MATH${i}\0`;
    })
    .replace(/(?<!\$)\$(?!\$)([\s\S]+?)(?<!\$)\$(?!\$)/g, (_full, body: string) => {
      const i = slots.length;
      slots.push(`$${escapeBarePercentInTex(body)}$`);
      return `\0MATH${i}\0`;
    });
  return { text: replaced, slots };
}

function restoreMathRegions(text: string, slots: string[]): string {
  return text.replace(/\0MATH(\d+)\0/g, (_m, idx: string) => slots[Number(idx)] ?? '');
}

/**
 * Wraps undelimited TeX (common in model table cells) in `$...$` so KaTeX runs.
 * Skips regions already inside `$` / `$$`.
 */
export function wrapBareTexInMarkdown(text: string): string {
  if (!text.includes('\\')) return text;
  const { text: protectedText, slots } = protectMathRegions(text);
  const wrapped = protectedText.replace(BARE_TEX_RE, (match) => {
    // Avoid wrapping leftover backslashes that are not real math.
    if (!/\\[a-zA-Z]/.test(match)) return match;
    // Keep trailing ) outside $...$ when the opening ( was prose (e.g. "Rs ( \dfrac... )").
    const trailingParens = match.match(/\)+$/)?.[0] ?? '';
    const core = match.slice(0, match.length - trailingParens.length).trim();
    if (!core) return match;
    return `$${escapeBarePercentInTex(core)}$${trailingParens}`;
  });
  return restoreMathRegions(wrapped, slots);
}

function sanitizeMathRegions(text: string): string {
  // Wrap bare TeX first (also escapes % inside newly wrapped math).
  let out = wrapBareTexInMarkdown(text);
  if (!out.includes('$')) return out;

  // Re-escape % in any remaining / already-delimited math.
  out = out.replace(/\$\$([\s\S]+?)\$\$/g, (_full, body: string) => {
    return `$$${escapeBarePercentInTex(body)}$$`;
  });
  out = out.replace(/(?<!\$)\$(?!\$)([\s\S]+?)(?<!\$)\$(?!\$)/g, (_full, body: string) => {
    return `$${escapeBarePercentInTex(body)}$`;
  });
  return out;
}

/**
 * Fixes common model LaTeX mistakes in markdown before KaTeX runs.
 * - Escapes bare `%` inside `$...$` / `$$...$$`
 * - Wraps undelimited TeX commands (e.g. `\dfrac{...}` in tables) in `$...$`
 * Skips fenced code so charts and snippets stay intact.
 */
export function sanitizeLatexInMarkdown(markdown: string): string {
  if (!markdown.includes('\\') && !markdown.includes('%')) return markdown;

  let result = '';
  let lastIndex = 0;
  const fenceRe = /```[\s\S]*?(?:```|$)/g;
  let match: RegExpExecArray | null;
  while ((match = fenceRe.exec(markdown)) !== null) {
    result += sanitizeMathRegions(markdown.slice(lastIndex, match.index));
    result += match[0];
    lastIndex = match.index + match[0].length;
  }
  result += sanitizeMathRegions(markdown.slice(lastIndex));
  return result;
}
